import { openModal } from '@/lib/modal';
import { createSchedule, updateSchedule, deleteSchedule } from '@/lib/data/schedule';
import { SCHEDULE_STATUS_LABEL, asOptions } from '@/lib/labels';
import { assignContractor } from '@/lib/data/subcontractors';
import type { RoomRow, ScheduleEntryRow, DesignerContactRow } from '@/types/database';
import { emptyToNull } from '@/lib/form-values';


/**
 * Shared schedule create/edit modal.
 *
 * Two modes:
 *   1. Room fixed — pass `roomId` and no `rooms`. Modal has no Room field.
 *      (Used by room-detail's Schedule tab.)
 *   2. Room picker — pass `rooms` and no `roomId`. Modal renders a required
 *      Room dropdown at the top. (Used by the project-level Schedule view.)
 *
 * When editing (`entry` provided), the entry's own `room_id` wins over both.
 */
export function openScheduleModal(opts: {
  roomId?: string;
  rooms?: RoomRow[];
  sortOrder: number;
  entry?: ScheduleEntryRow;
  /**
   * Contacts eligible to be assigned. Only offered when CREATING: assigning on
   * an existing task belongs in the contractor portal panel, where the
   * assignment can also be removed.
   */
  contractors?: DesignerContactRow[];
  onDone: () => void;
}): void {
  const { rooms, entry } = opts;
  const s = entry;
  const usePicker = !opts.roomId && !!rooms;
  const initialRoomId = s?.room_id ?? opts.roomId ?? rooms?.[0]?.id ?? '';

  const fields = [
    ...(usePicker ? [{
      name: 'room_id',
      label: 'Room',
      type: 'select' as const,
      required: true,
      defaultValue: initialRoomId,
      options: (rooms ?? []).map((r) => ({ value: r.id, label: r.name })),
    }] : []),
    { name: 'task', label: 'Task', required: true, defaultValue: s?.task },
    { name: 'start_date', label: 'Start', type: 'date' as const, defaultValue: s?.start_date },
    { name: 'end_date', label: 'End', type: 'date' as const, defaultValue: s?.end_date },
    // Status is hidden when creating (a new task is always 'planned'); it stays
    // editable when editing an existing entry, and from the Schedule view's
    // inline status chip.
    ...(s ? [{
      name: 'status', label: 'Status', type: 'select' as const,
      options: asOptions(SCHEDULE_STATUS_LABEL),
      defaultValue: s.status,
    }] : []),
    { name: 'notes', label: 'Notes', type: 'textarea' as const, voice: true, defaultValue: s?.notes },
    // Assigning here is what makes the reminder queue fill: creating a
    // contractor_assignments row fires the enqueue trigger from 0013, which
    // queues the 7-day / 2-day / day-of reminders.
    ...(!s && opts.contractors?.length ? [{
      name: 'contractor_id', label: 'Assign contractor', type: 'select' as const,
      options: [
        { value: '', label: '— Nobody yet —' },
        ...opts.contractors.map((c) => ({ value: c.id, label: c.name })),
      ],
      defaultValue: '',
    }] : []),
  ];

  openModal({
    title: s ? 'Edit task' : 'Add task',
    showDelete: !!s,
    fields,
    onSubmit: async (v) => {
      const payload = {
        task: String(v.task),
        start_date: emptyToNull(v.start_date),
        end_date: emptyToNull(v.end_date),
        // Status field is only rendered when editing — new tasks start planned.
        status: (v.status ?? 'planned') as ScheduleEntryRow['status'],
        notes: emptyToNull(v.notes),
      };
      if (s) {
        await updateSchedule(s.id, payload);
      } else {
        const targetRoom = usePicker ? String(v.room_id) : opts.roomId!;
        const created = await createSchedule({
          room_id: targetRoom,
          sort_order: opts.sortOrder,
          ...payload,
        });
        const contractorId = String(v.contractor_id ?? '');
        if (contractorId) {
          // Best effort: the task itself is the product. If the assignment
          // fails the designer can add it from the contractor panel, and
          // losing the task because a reminder couldn't be queued would be the
          // worse trade.
          try {
            await assignContractor(contractorId, created.id);
          } catch (err) {
            console.warn('[schedule] contractor assignment skipped:', err);
          }
        }
      }
      opts.onDone();
    },
    onDelete: s ? async () => { await deleteSchedule(s.id); opts.onDone(); } : undefined,
  });
}
